/**
 * StructuredData — injects a schema.org JSON-LD block into <head> so search
 * engines and social crawlers understand the gallery's artworks, artists
 * and exhibitions. One instance per page; safe to render multiple times
 * (each gets its own <script type="application/ld+json">).
 */
import { useEffect, useRef } from 'react';

export default function StructuredData({ data }) {
    const nodeRef = useRef(null);
    useEffect(() => {
        if (!data) return;
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.text = JSON.stringify(data);
        document.head.appendChild(s);
        nodeRef.current = s;
        return () => {
            if (nodeRef.current && nodeRef.current.parentNode) {
                nodeRef.current.parentNode.removeChild(nodeRef.current);
            }
        };
    }, [data]);
    return null;
}

export const galleryLd = () => ({
    "@context": "https://schema.org",
    "@type": "ArtGallery",
    "name": "Maison Touré",
    "url": "https://maisontoure.com/",
    "logo": "https://maisontoure.com/maison-toure-logo-1200.png",
    "image": "https://maisontoure.com/maison-toure-open-graph-2026-07.jpg",
    "description": "A Sydney gallery for the considered presentation of contemporary African art — painting, sculpture, photography, textile. Provenance-led. Privately viewed.",
    "areaServed": ["AU", "Worldwide"],
    "address": { "@type": "PostalAddress", "addressLocality": "Sydney", "addressRegion": "NSW", "addressCountry": "AU" },
    // `sameAs` — confirmed official Maison Touré social profiles.
    "sameAs": [
        "https://www.instagram.com/maisontoure.art/",
        "https://www.facebook.com/maisontoure.art",
    ],
});

export const artworkLd = (w) => ({
    "@context": "https://schema.org",
    "@type": "VisualArtwork",
    "name": w.title,
    "artform": w.medium,
    "artworkSurface": w.medium,
    "creator": { "@type": "Person", "name": w.artist_name },
    "dateCreated": String(w.year || ''),
    "image": w.image,
    ...(w.price && !w.price_on_request ? {
        "offers": { "@type": "Offer", "price": w.price, "priceCurrency": w.currency || "AUD", "availability": w.availability === "Available" ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
    } : {}),
});

export const artistLd = (a) => ({
    "@context": "https://schema.org",
    "@type": "Person",
    "name": a.name,
    "description": a.statement,
    "image": a.portrait,
    "birthPlace": a.origin,
    "jobTitle": "Artist",
});

export const exhibitionLd = (e) => ({
    "@context": "https://schema.org",
    "@type": "ExhibitionEvent",
    "name": e.title,
    "description": e.description,
    "startDate": e.start_date,
    "endDate": e.end_date,
    "location": { "@type": "Place", "name": e.location || "Maison Touré, Sydney" },
    "image": e.cover_image,
});

export const journalLd = (j) => ({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": j.title,
    "description": j.excerpt || j.subtitle,
    "image": j.cover_image,
    "datePublished": j.published_at || j.created_at,
    "dateModified": j.updated_at || j.published_at || j.created_at,
    "author": { "@type": "Person", "name": j.author || "Fatym Touré" },
    "publisher": {
        "@type": "Organization",
        "name": "Maison Touré",
        "logo": { "@type": "ImageObject", "url": "" },
    },
});
